import { createContext, useContext, useState, useEffect } from 'react';
import { rememberRole, roleHistory } from '../lib/roleHistory';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const getAuthStorage = () => {
    try {
      const storageType = localStorage.getItem('authStorage');
      return storageType === 'session' ? sessionStorage : localStorage;
    } catch { return localStorage; }
  };

  const [user, setUserState] = useState(null);
  // Role is persisted synchronously so the role-less URL history can map
  // the address bar to the right role tree on the very next navigation
  // (and on a hard reload, before /auth/me returns).
  const setUser = (u) => {
    rememberRole(u?.role || null);
    setUserState(u);
    if (u?.role) roleHistory.cleanCurrentUrl();
  };
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('token') || sessionStorage.getItem('token') || null;
    } catch (e) {
      console.warn('Storage not available:', e);
      return null;
    }
  });
  const [refreshToken, setRefreshToken] = useState(() => {
    try {
      return localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken') || null;
    } catch (e) {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');

      // iter318 wave 20c — Always try `/auth/me`, even without a
      // localStorage token. The HttpOnly `access_token` cookie set by
      // the login / handoff endpoints authenticates the request all
      // by itself, so cookie-only flows (e.g. cross-tenant handoff,
      // any future SSR path) can boot up an authenticated session
      // without needing JS-readable tokens.
      //
      // Anonymous visitors on the marketing site legitimately hit a
      // 401 here — the response interceptor already special-cases
      // `/auth/me` to skip the refresh + redirect dance, so this is
      // side-effect free when no session exists.

      // Retry logic for flaky mobile connections
      const maxRetries = 3;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 10000);

          const response = await api.get('/auth/me', { signal: controller.signal });
          clearTimeout(timeout);

          setUser(response.data);
          // Sync token state in case interceptor refreshed it
          const currentToken = getAuthStorage().getItem('token');
          if (currentToken && currentToken !== token) {
            setToken(currentToken);
          }
          setLoading(false);
          return; // Success — exit
        } catch (error) {
          const is401 = error.response?.status === 401;
          const isNetworkError = !error.response && error.code !== 'ERR_CANCELED';
          const isTimeout = error.name === 'AbortError' || error.code === 'ERR_CANCELED';
          const is503 = error.response?.status === 503;

          // If 401 was handled by interceptor (refresh succeeded + retry succeeded),
          // we won't be here — the retried request would have resolved above.
          // If we're here with 401, either (a) we never had a session, or
          // (b) refresh also failed → interceptor already gave up.
          if (is401) {
            // Signed out: forget the stored role, otherwise the role-less URL
            // layer keeps mapping "/" to that role's dashboard → ProtectedRoute
            // bounces to /?tab=login → which maps back to the dashboard (blank page).
            setUser(null);
            // Only clear storage if there was actually something to clear;
            // anonymous marketing visitors legitimately hit this path.
            if (storedToken) {
              const storage = getAuthStorage();
              if (!storage?.getItem('refreshToken') && !storage?.getItem('token')) {
                clearAuth();
              }
            }
            break; // Don't retry on auth failures
          }

          // Network error, timeout, or 503 (SW offline response) — retry
          if ((isNetworkError || isTimeout || is503) && attempt < maxRetries) {
            await new Promise(r => setTimeout(r, 1000 * attempt)); // backoff
            continue;
          }

          // Final attempt failed but tokens still in storage — don't clear, just stop loading
          // User will see the page and can interact; next API call will retry auth
          break;
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const clearAuth = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('authStorage');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
    } catch (e) {
      console.warn('Failed to clear storage:', e);
    }
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  const login = async (email, password, rememberMe = true) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, refresh_token, user: userData } = response.data;

    // Supersede any pre-existing session before persisting the new one.
    // On the shared platform host (www.vyomos.io) a stale super-admin
    // token could otherwise linger in the other storage bucket and be
    // picked up by AuthContext's `localStorage || sessionStorage` getter,
    // hijacking a fresh tenant-admin login. Clearing both buckets makes
    // the newly-authenticated identity win deterministically.
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
    } catch (e) { /* storage unavailable */ }

    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem('token', access_token);
    if (refresh_token) {
      storage.setItem('refreshToken', refresh_token);
      setRefreshToken(refresh_token);
    }
    // Track which storage is being used
    localStorage.setItem('authStorage', rememberMe ? 'local' : 'session');
    
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  // Set auth state directly (used after claim account, social login, etc.)
  const setAuth = (accessToken, userData, newRefreshToken = null) => {
    const storageType = localStorage.getItem('authStorage') || 'local';
    const storage = storageType === 'session' ? sessionStorage : localStorage;
    storage.setItem('token', accessToken);
    if (newRefreshToken) {
      storage.setItem('refreshToken', newRefreshToken);
      setRefreshToken(newRefreshToken);
    }
    setToken(accessToken);
    setUser(userData);
  };

  const register = async (data) => {
    const response = await api.post('/auth/register', data);
    const { access_token, refresh_token, user: userData } = response.data;
    
    // Registration always uses persistent storage
    localStorage.setItem('token', access_token);
    localStorage.setItem('authStorage', 'local');
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
      setRefreshToken(refresh_token);
    }
    
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async (logoutAllDevices = false) => {
    try {
      // Notify backend to revoke refresh token
      if (refreshToken || logoutAllDevices) {
        await api.post('/auth/logout', { 
          refresh_token: refreshToken,
          logout_all_devices: logoutAllDevices
        });
      }
    } catch (error) {
      console.warn('Logout API call failed:', error);
      // Continue with local logout even if API fails
    }
    clearAuth();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      refreshToken,
      loading, 
      login, 
      setAuth, 
      register, 
      logout 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
